import { getLatestAsset } from '@folio/utils/useCurrentActiveAsset';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  keyValueFieldsToObject,
  policyControllerCalculatePolicyQuoteQueryOptions,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import _ from 'lodash';
import { parseWakamError } from '@folio/utils/parseWakamError';

export function useWakamDashboardError({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): string | null {
  const { policy, workingBranch } = usePolicyOnDashboard();

  const { data: existingAsset } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy?.id, {
      branchId: workingBranch?.id,
    }),
    enabled,
    select: data =>
      getLatestAsset(
        data.data.map(asset => ({
          ...asset,
          customFields: keyValueFieldsToObject(asset.customFields ?? []),
        })),
      ),
  });

  const existingOptions = existingAsset?.quote?.options;
  const quoteParams = {
    assetIds: [existingAsset?.id!],
    options: [
      {
        assetId: existingAsset?.id!,
        tier: existingOptions?.tier ?? 'BASIC',
        excess: existingOptions?.excess ?? 'DEFAULT',
        perils: existingOptions?.perils?.map(({ id }: { id: string }) => ({ id })) ?? [],
      },
    ],
    customFieldsToOverride: [],
    brokerageFeesOverride: [],
  };

  const { error } = useQuery({
    ...policyControllerCalculatePolicyQuoteQueryOptions(policy?.id, quoteParams, {
      branchId: workingBranch?.id,
    }),
    enabled: enabled && !!policy?.brokerageFirm && !!existingAsset?.id && !!workingBranch?.id,
    retry: false,
    meta: { disableDefaultErrorHandling: true },
  });

  const rawWakamError =
    _.get(error, 'errorResponse.data.details.quoteErrors.0.error') ??
    _.get(error, 'errorResponse.data.details.failedReason') ??
    _.get(error, 'errorResponse.data.details.errorMessage');

  return typeof rawWakamError === 'string' ? parseWakamError(rawWakamError) : null;
}
